import { Colli } from '@frontend/colli/types';
import { SlotRendering } from '@frontend/common-types';
import { Logger } from '@frontend/logger';
import { ModuleClient } from '@frontend/module/api';
import { Module } from '@frontend/module/types';
import { PackageClient, PackageWorkflowClient } from '@frontend/package/api';
import { Package, PackageTriggerName } from '@frontend/package/types';
import { SlotClient, SlotWorkflowClient } from '@frontend/slot/api';
import { Slot, SlotType } from '@frontend/slot/types';
import { ToastUtil } from '@frontend/toast-utils';
import { TransactionWorkflowClient } from '@frontend/transaction/api';
import { TransactionTriggerName } from '@frontend/transaction/types';
import { ScreenTrigger, ScreenType, Step } from '@frontend/wizard/types';
import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { SlotLayoutCell, slotLayoutDisplayNumber } from '../../../components/slot-layout-cell';
import { useSlotListener } from '../../../hooks/useSlotListener/useSlotListener';
import { useWizardPackage } from '../../hooks/useWizardPackage';
import { useWizardTransaction } from '../../hooks/useWizardTransaction';
import { fetchOpenableDropOffPackages, isOpenableDropOffPackage } from '../../utils/seed-drop-off-colli-context';
import { useScreen } from '../useScreen';

const moveSlotFailedToast = (
    <FormattedMessage
        id='kiosk.wizard.drop-off-confirm-open-slot.move-slot-failed'
        defaultMessage='Could not change SLOT. Please try again.'
        description='Error toast when changing the drop-off SLOT fails on the confirm open SLOT screen'
    />
);

interface ViewProps {
    accountId?: string;
    spotId?: string;
    currentModule: Module | null;
    modules: Module[] | null;
    selectedSlot: Slot | null;
    setCurrentModule: (module: Module | null) => void;
    slotComponent: (slotRendering: SlotRendering) => React.ReactNode;
    submitDisabled: boolean;
    onSubmit: () => void;
    onBack: (screenType: ScreenType) => void;

    selectModuleModalOpen: boolean;
    setSelectModuleModalOpen: (open: boolean) => void;
}

const useDropOffConfirmOpenSlot = (): ViewProps => {
    const { navigationState, store, updateWizardData, onBack, onSubmit: onContinue } = useScreen();
    const { transaction } = useWizardTransaction({
        wizardId: store!.wizard.id,
        store
    });
    const {
        package: _package,
        localUpdatePackage,
        refresh
    } = useWizardPackage({
        wizardId: store!.wizard.id,
        store
    });

    const [modules, setModules] = useState<Module[] | null>(null);
    const [currentModuleIndex, setCurrentModuleIndex] = useState<number>(0);

    const [allSlots, setAllSlots] = useState<Slot[] | null>(null);
    const [availableSlots, setAvailableSlots] = useState<Slot[] | null>(null);
    const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);

    const [selectModuleModalOpen, setSelectModuleModalOpen] = useState<boolean>(false);

    // Ref to store the resolve function for the move slot promise
    const [isMoving, setIsMoving] = useState<Slot | null>(null);
    const moveSlotResolveRef = useRef<(() => void) | null>(null);
    const canMove =
        !!_package &&
        !_package.state?.includes('done') &&
        !_package.state?.includes('skipped') &&
        !_package.state?.includes('cancelled') &&
        !_package.state?.includes('failed');

    useSlotListener({
        id: 'DROP_OFF_CONFIRM_OPEN_SLOT',
        onPackageSlotMoved: (pkg) => {
            if (_package && pkg.id === _package.id) {
                if (moveSlotResolveRef.current) {
                    moveSlotResolveRef.current();
                    moveSlotResolveRef.current = null;
                }
            }
        }
    });

    // Fetch spot and modules
    useEffect(() => {
        const accountId = navigationState.iot?.account_id || _package?.account_id || transaction?.account_id;
        const spotId = navigationState.iot?.spot_id || _package?.spot_id || transaction?.spot_id;
        const moduleId = _package?.module_id || transaction?.module_id;

        if (accountId && spotId) {
            ModuleClient.fetchModules({ spot_id: spotId }).then((result) => {
                // Sort modules by name using ASCII comparison (handles both numeric and alphabetic: 1, 2, 3... or a, b, c...)
                const sortedModules = result.results.sort((a, b) => {
                    const indexA = a.index || '';
                    const indexB = b.index || '';
                    return indexA.localeCompare(indexB, undefined, { numeric: true, sensitivity: 'base' });
                });
                setModules(sortedModules);
                // Find the module that contains a package's slot
                if (moduleId) {
                    const moduleIndex = sortedModules.findIndex((m) => m.id === moduleId);
                    if (moduleIndex >= 0) {
                        setCurrentModuleIndex(moduleIndex);
                    }
                }
            });

            SlotClient.fetchSlots({ spot_id: spotId, size: '1000', index: '0' }).then((result) => {
                setAllSlots(result.results);
            });

            SlotWorkflowClient.postAvailableSlots(accountId, spotId, {
                account_id: accountId,
                application: store?.functionality?.application,
                feature: store?.functionality?.feature,
                functionality_id: store?.functionality?.id,
                transaction_type: transaction?.type
            }).then((result) => {
                setAvailableSlots(result.results);
            });
        }
    }, []);

    useEffect(() => {
        if (_package || !transaction?.id || !navigationState.iot?.account_id) return;

        const seededPackages = ((store?.data.packages as Package[] | undefined) ?? []).filter(isOpenableDropOffPackage);
        const collis = (store?.data.collis as Colli[] | undefined) ?? [];
        const spotId = navigationState.spot?.id ?? navigationState.iot.spot_id;

        const applyPackages = (packages: Package[]) => {
            if (packages.length === 0) return;
            updateWizardData({ packages });
            localUpdatePackage(packages[0]);
        };

        if (seededPackages.length > 0) {
            applyPackages(seededPackages);
            return;
        }

        fetchOpenableDropOffPackages({
            accountId: navigationState.iot.account_id,
            transactionId: transaction.id,
            spotId,
            colliIds: collis.length > 0 ? collis.map((c) => c.id) : undefined
        }).then(applyPackages);
    }, [transaction, _package]);

    useEffect(() => {
        if (availableSlots && allSlots && store && modules) {
            if (_package?.slot_id) {
                const selected = allSlots.find((slot) => slot.id === _package.slot_id);
                if (selected) {
                    setSelectedSlot(selected);
                    setCurrentModuleIndex(modules.findIndex((m) => m.id === selected.module_id));
                }
            } else if (transaction?.slot_id) {
                const selected = allSlots.find((slot) => slot.id === transaction.slot_id);
                if (selected) {
                    setSelectedSlot(selected);
                    setCurrentModuleIndex(modules.findIndex((m) => m.id === selected.module_id));
                }
            }
        }
    }, [availableSlots, allSlots, modules, _package, transaction]);

    const currentModule = modules && modules.length > 0 ? modules[currentModuleIndex] : null;

    const setCurrentModule = (module: Module | null) => {
        if (modules && module) {
            const index = modules.findIndex((m) => m.id === module.id);
            if (index >= 0) {
                setCurrentModuleIndex(index);
            }
        }
        setSelectModuleModalOpen(false);
    };

    const slotComponent = (slotRendering: SlotRendering): React.ReactNode => {
        const slot = allSlots ? allSlots.find((s) => s.id === slotRendering.id) : null;
        const isHighlighted = slot?.id === selectedSlot?.id;
        const isAvailable = !!availableSlots?.find((s) => s.id === slot?.id);
        const slotNumber = slotLayoutDisplayNumber(slot);
        const isLoading = !!(isMoving && slot && isMoving.id === slot.id);
        const canSelectForMove = canMove && isAvailable && !!slot && isMoving === null;

        return (
            <SlotLayoutCell
                slotNumber={slotNumber}
                isSelected={!!isHighlighted}
                isDisabled={slot?.type === SlotType.TERMINAL}
                hasContent={canSelectForMove}
                isLoading={isLoading}
                onSelect={isHighlighted && !isMoving ? onSubmit : canSelectForMove && slot ? () => onMoveSlot(slot) : undefined}
            />
        );
    };

    const resolvePackagesToOpen = (): Package[] => {
        const seeded = ((store?.data.packages as Package[] | undefined) ?? []).filter(isOpenableDropOffPackage);
        if (seeded.length > 0) return seeded;
        if (_package?.account_id && _package.transaction_id && _package.id) {
            return [_package as Package];
        }
        return [];
    };

    const resolveNextStep = (packagesToOpen: Package[]): Step | undefined => {
        const submitStep = store?.steps.find((step) => step.screen === ScreenType.DROP_OFF_CONFIRM_OPEN_SLOT && step.trigger === ScreenTrigger.SUBMIT);
        if (packagesToOpen.length > 1) {
            const multiStep = store?.steps.find(
                (step) => step.screen === ScreenType.DROP_OFF_CONFIRM_OPEN_SLOT && step.next_screen === ScreenType.PROCESS_OPEN_CLOSE_SLOTS
            );
            if (multiStep) return multiStep;
            // Multi-door drop-off: use plural process screen even if wizard graph still points at singular.
            if (submitStep) {
                return { ...submitStep, next_screen: ScreenType.PROCESS_OPEN_CLOSE_SLOTS };
            }
        }
        return submitStep;
    };

    const onSubmit = async () => {
        if (!store || !selectedSlot || !navigationState.iot) return;

        const packagesToOpen = resolvePackagesToOpen();
        if (packagesToOpen.length === 0) return;

        updateWizardData({ packages: packagesToOpen });

        await Promise.all(
            packagesToOpen.map((pkg) =>
                PackageWorkflowClient.triggerPackageState(pkg.account_id, pkg.transaction_id, pkg.id, {
                    trigger: PackageTriggerName.PACKAGE_OPEN_SLOT,
                    location: navigationState.spot?.id ?? undefined,
                    actor: navigationState.user?.id ?? undefined
                })
            )
        ).finally(() => {
            onContinue(resolveNextStep(packagesToOpen));
        });
    };

    const onMoveSlot = async (slot: Slot) => {
        if (isMoving || !canMove || !_package || !_package.account_id || !_package.transaction_id || !_package.id || !navigationState.iot) {
            return;
        }
        if (_package.slot_id === slot.id) {
            setSelectedSlot(slot);
            return;
        }

        const accountId = _package.account_id;
        const transactionId = _package.transaction_id;
        const packageId = _package.id;
        const spotId = _package.spot_id;

        setIsMoving(slot);
        try {
            const moveCompletePromise = new Promise<void>((resolve, reject) => {
                const timeoutId = window.setTimeout(() => {
                    moveSlotResolveRef.current = null;
                    reject(new Error('Timed out waiting for package slot moved'));
                }, 15000);
                moveSlotResolveRef.current = () => {
                    window.clearTimeout(timeoutId);
                    resolve();
                };
            });

            await TransactionWorkflowClient.triggerTransactionState(accountId, transactionId, {
                trigger: TransactionTriggerName.TRANSACTION_MOVE_SLOT,
                location: navigationState.spot?.id ?? undefined,
                actor: navigationState.user?.id ?? undefined,
                data: {
                    spot_id: slot.spot_id,
                    new_module_id: slot.module_id,
                    new_slot_id: slot.id
                }
            });

            try {
                await moveCompletePromise;
            } catch {
                // MQTT may have been missed; fall back to refreshing package state.
                await refresh().catch((err) => {
                    Logger.error('Failed to refresh package after drop-off SLOT move timeout', err);
                });
                const verified = await PackageClient.fetchPackage(accountId, transactionId, packageId).catch((err) => {
                    Logger.error('Failed to verify package SLOT after drop-off move timeout', err);
                    return null;
                });
                if (verified?.slot_id !== slot.id) {
                    Logger.error('Drop-off SLOT move did not complete', { expectedSlotId: slot.id, actualSlotId: verified?.slot_id });
                    ToastUtil.kioskError(moveSlotFailedToast);
                    return;
                }
            }

            await refresh().catch((err) => {
                Logger.error('Failed to refresh package after drop-off SLOT move', err);
            });
            setSelectedSlot(slot);
            setCurrentModuleIndex(modules?.findIndex((m) => m.id === slot.module_id) ?? 0);

            if (spotId) {
                const result = await SlotWorkflowClient.postAvailableSlots(accountId, spotId, {
                    account_id: accountId,
                    application: store?.functionality?.application,
                    feature: store?.functionality?.feature,
                    functionality_id: store?.functionality?.id,
                    transaction_type: transaction?.type
                });
                setAvailableSlots(result.results);
            }
        } catch (error) {
            Logger.error('Failed to move drop-off SLOT', error);
            ToastUtil.kioskError(moveSlotFailedToast);
        } finally {
            moveSlotResolveRef.current = null;
            setIsMoving(null);
        }
    };

    return {
        accountId: navigationState.iot?.account_id || _package?.account_id || transaction?.account_id || undefined,
        spotId: navigationState.iot?.spot_id || _package?.spot_id || transaction?.spot_id || undefined,
        currentModule,
        modules,
        selectedSlot,
        setCurrentModule,
        slotComponent,
        submitDisabled: !selectedSlot || isMoving !== null,
        onSubmit,
        onBack,

        selectModuleModalOpen,
        setSelectModuleModalOpen
    };
};

export default useDropOffConfirmOpenSlot;
